import type { WelcomeEmailProps } from "./types";

const FONT = "Arial, Helvetica, sans-serif";

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function tile(icon: string, title: string, desc: string): string {
  return `
  <td valign="top" width="50%" style="padding:6px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #E5E7EB;border-radius:8px;">
      <tr><td valign="top" style="padding:16px;">
        <div style="font-family:${FONT};font-size:24px;line-height:28px;">${icon}</div>
        <div style="font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;line-height:20px;margin-top:8px;">${esc(title)}</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:13px;line-height:20px;margin-top:4px;">${esc(desc)}</div>
      </td></tr>
    </table>
  </td>`;
}

function step(num: number, title: string, desc: string, linkUrl: string, linkLabel: string, done: boolean): string {
  const status = done
    ? `<span style="font-family:${FONT};color:#16A34A;font-size:12px;font-weight:bold;">&#10003; Done</span>`
    : `<a href="${esc(linkUrl)}" style="font-family:${FONT};color:#2E75B6;font-size:13px;text-decoration:none;">${esc(linkLabel)}</a>`;
  return `
  <tr>
    <td style="padding:14px 0;border-bottom:1px solid #E5E7EB;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td width="36" valign="middle" style="background-color:#D4A843;border-radius:18px;font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;text-align:center;line-height:36px;height:36px;">${num}</td>
          <td valign="middle" style="padding:0 16px;">
            <div style="font-family:${FONT};color:#1F3864;font-size:15px;font-weight:bold;line-height:20px;">${esc(title)}</div>
            <div style="font-family:${FONT};color:#6B7280;font-size:13px;line-height:20px;margin-top:4px;">${esc(desc)}</div>
          </td>
          <td valign="middle" align="right" style="white-space:nowrap;">${status}</td>
        </tr>
      </table>
    </td>
  </tr>`;
}

export function generateWelcomeEmailHTML(p: WelcomeEmailProps): string {
  const isGroup = p.segment === "grouphome";

  const tiles = [
    tile(
      "&#128172;",
      isGroup ? "Your AI Intake Coordinator" : "Sarah, Your AI Care Coordinator",
      isGroup
        ? "Captures every placement inquiry automatically — day or night."
        : "Captures and qualifies every lead 24/7 — even while you sleep.",
    ),
    tile(
      "&#127919;",
      "Predictive Lead Scoring",
      isGroup
        ? "Every inquiry scored 0–100 instantly. Urgent placements alert you immediately."
        : "Every lead scored 0–100 instantly. Hot leads go straight to your phone.",
    ),
    tile(
      "&#128231;",
      isGroup ? "10 Referral Email Templates" : "14 Referral Email Templates",
      isGroup
        ? "Personalized outreach emails for support coordinators, CSBs, and MCOs — ready to send."
        : "Personalized outreach emails for every referral source — ready to send.",
    ),
    tile(
      "&#9889;",
      "Automated Lead Enrichment",
      "60 seconds after a lead comes in, our AI enriches it and updates the score automatically.",
    ),
  ];

  const steps = [
    step(
      1,
      "Complete your setup",
      "Add your service area, specialties, and notification preferences so your scoring engine is calibrated to your business.",
      p.onboardingUrl,
      "Finish Setup &rarr;",
      p.isOnboardingComplete,
    ),
    step(
      2,
      isGroup ? "Embed your intake coordinator on your website" : "Embed Sarah on your website",
      "Copy one line of code and paste it into your website. Your AI intake coordinator will start capturing leads immediately.",
      p.chatbotEmbedUrl,
      "Get Embed Code &rarr;",
      false,
    ),
    step(
      3,
      "Send your first referral outreach email",
      isGroup
        ? "Choose a referral source type, pick a template, and reach out to one support coordinator or CSB today."
        : "Choose a referral source type, pick a template, and send a personalized email to one referral partner today.",
      `${p.dashboardUrl}/outreach`,
      "Go to Outreach &rarr;",
      false,
    ),
  ].join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Welcome to LegacyGrowth Engine</title>
</head>
<body style="margin:0;padding:0;background-color:#F0F2F5;font-family:${FONT};">
<div style="display:none;max-height:0;overflow:hidden;color:transparent;visibility:hidden;mso-hide:all;font-size:1px;line-height:1px;">Your 14-day free trial has started. Here is exactly what to do first.</div>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F0F2F5;">
  <tr><td align="center" style="padding:24px 12px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background-color:#FFFFFF;border-radius:8px;">

      <tr><td style="background-color:#0F1E35;padding:32px 40px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td valign="middle" style="font-family:${FONT};color:#D4A843;font-size:18px;font-weight:bold;line-height:24px;">LegacyGrowth Engine</td>
            <td valign="middle" align="right">
              <span style="background-color:#1F3864;color:#D4A843;font-family:${FONT};font-size:11px;font-weight:bold;padding:4px 10px;border-radius:4px;display:inline-block;">${esc(p.plan)} Plan &mdash; Free Trial</span>
            </td>
          </tr>
        </table>
        <div style="border-top:1px solid #2E3D55;margin:18px 0 12px;"></div>
        <div style="font-family:${FONT};font-size:32px;line-height:36px;text-align:center;padding:8px 0;">&#127881;</div>
        <div style="font-family:${FONT};color:#FFFFFF;font-size:28px;font-weight:bold;line-height:36px;text-align:center;">You&#39;re in, ${esc(p.ownerName)}.</div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:15px;line-height:24px;text-align:center;margin-top:8px;">LegacyGrowth Engine is ready. Now let&#39;s make it work for ${esc(p.agencyName)}.</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:18px auto 0;">
          <tr><td align="center" style="background-color:#1F3864;border-radius:6px;padding:12px 20px;font-family:${FONT};color:#D4A843;font-size:13px;font-weight:bold;text-align:center;line-height:18px;">
            &#9201;&nbsp; ${esc(p.trialDaysRemaining)} days left in your free trial &mdash; expires ${esc(p.trialEndDate)}
          </td></tr>
        </table>
      </td></tr>

      <tr><td style="background-color:#FFFFFF;padding:32px 40px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:16px;">Here is what is ready for you right now</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>${tiles[0]}${tiles[1]}</tr>
          <tr>${tiles[2]}${tiles[3]}</tr>
        </table>
      </td></tr>

      <tr><td style="background-color:#F8F9FB;border-top:1px solid #E5E7EB;border-bottom:1px solid #E5E7EB;padding:32px 40px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:12px;">Do these three things today</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${steps}</table>
      </td></tr>

      <tr><td style="background-color:#FFFFFF;padding:28px 40px 36px;" align="center">
        <div style="font-family:${FONT};color:#1F3864;font-size:18px;font-weight:bold;line-height:24px;text-align:center;">Your dashboard is ready.</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:14px;line-height:22px;text-align:center;margin-top:8px;">Everything is set up and waiting for you. Log in and complete step one to start capturing ${esc(p.segment_label)} today.</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:20px auto 0;">
          <tr><td align="center" style="background-color:#D4A843;border-radius:6px;">
            <a href="${esc(p.dashboardUrl)}" style="display:inline-block;padding:14px 28px;font-family:${FONT};color:#1F3864;font-size:15px;font-weight:bold;text-decoration:none;line-height:20px;">Go to My Dashboard &rarr;</a>
          </td></tr>
        </table>
        <div style="font-family:${FONT};color:#6B7280;font-size:13px;line-height:20px;text-align:center;margin-top:18px;">Need help getting started? Reply to this email or contact us at <a href="mailto:${esc(p.supportEmail)}" style="color:#2E75B6;text-decoration:underline;">${esc(p.supportEmail)}</a>.</div>
      </td></tr>

      <tr><td style="background-color:#0F1E35;padding:24px 40px;">
        <div style="font-family:${FONT};line-height:20px;">
          <span style="color:#D4A843;font-size:14px;font-weight:bold;">LegacyGrowth Engine</span><span style="color:#9AAFC8;font-size:14px;"> &mdash; A product of H&amp;E Associates LLC</span>
        </div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:12px;line-height:20px;margin-top:6px;">McLean, Virginia &nbsp;&middot;&nbsp; ${esc(p.supportEmail)}</div>
        <div style="margin-top:10px;font-family:${FONT};font-size:12px;line-height:18px;">
          <a href="mailto:${esc(p.supportEmail)}" style="color:#D4A843;text-decoration:underline;">Contact Support</a>
          <span style="color:#9AAFC8;"> &middot; </span>
          <a href="${esc(p.unsubscribeUrl)}" style="color:#9AAFC8;text-decoration:underline;">Unsubscribe</a>
        </div>
        <div style="font-family:${FONT};color:#4B5563;font-size:11px;line-height:18px;margin-top:12px;">You are receiving this email because you created a LegacyGrowth Engine account at ${esc(p.agencyName)}. This is a transactional email related to your account &mdash; you cannot unsubscribe from account setup emails. To close your account, contact ${esc(p.supportEmail)}.</div>
      </td></tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;
}
